/**
 * The server-sent-events wire format, read rather than written, and the reconnect policy over it.
 *
 * Nothing here touches the network or the DOM, so the whole of it is tested without a browser.
 *
 * ## Why this file exists at all, when `EventSource` is a browser built-in
 *
 * `EventSource` cannot send a request header. Its constructor takes a URL and a `withCredentials`
 * flag and nothing else, and the reconnect it performs on its own is a fresh request built from that
 * same URL — so a header set once could not survive it even if one could be set.
 *
 * EXP-003's offer stream is `bearerAuth` only. Its own contract says so:
 * `security: [{ bearerAuth: ["driver-app:stream"] }]`, and its 401 is "No valid bearer access
 * token". There is no cookie session on this platform's APIs and no token query parameter, and
 * inventing either would be inventing a payload. So **`EventSource` cannot open this stream**, and
 * the honest consequence is that the framing, the `retry:` field and the `Last-Event-ID` replay —
 * all four of which ADR 0024 chose server-sent events partly in order to inherit from the standard —
 * have to be implemented here instead. `fetch` can carry the header; its body is a byte stream; and
 * what is below turns that stream back into events. Recorded as R-29.
 *
 * That is a smaller thing than it sounds and a real finding either way: ADR 0024's consequences say
 * "a client consumes this with `EventSource` or `fetch` and a reader", and only the second of those
 * two is actually available to a browser against this contract.
 *
 * ## The one hazard in the format
 *
 * A line is `field: value`, a blank line dispatches the event, and a line beginning `:` is a comment
 * — which is what a heartbeat is, so a heartbeat must dispatch nothing. `data` accumulates across
 * lines joined by `\n`; `id` and `event` do not. A `\r\n`, a bare `\r` and a bare `\n` are all line
 * terminators, and a chunk boundary can fall inside any of them — including between the `\r` and the
 * `\n` of one terminator, which is why the buffer below holds a trailing `\r` back rather than
 * treating it as a complete line.
 */

/** One dispatched event, as the application reads it. */
export interface ServerSentEvent {
  /** The `event:` field, or `message` when the stream sent none, as the standard requires. */
  readonly name: string;
  /** The `data:` field, lines joined with `\n`. Empty when the stream sent none. */
  readonly data: string;
  /** The `id:` field, if this event carried one. This stream's cursor. */
  readonly id?: string;
}

/**
 * A chunk-at-a-time reader for one connection.
 *
 * Stateful by necessity — a chunk is not a line and a line is not an event — and deliberately not a
 * generator: the caller is an `await` loop over a `ReadableStream` and wants to hand bytes in and
 * get whole events out.
 */
export class EventStreamReader {
  private buffer = "";
  private name: string | undefined;
  private data: string[] = [];
  private id: string | undefined;
  /**
   * The last `retry:` the stream asked for, in milliseconds.
   *
   * EXP-003 writes it as the first line of the body, before any event, and it is the interval a
   * client is meant to wait before reconnecting. Held here rather than in the policy below so the
   * policy stays a pure function of what it is given.
   */
  public retryMs: number | undefined;

  /** Feed one chunk of decoded text in; take whatever events it completed out. */
  public push(chunk: string): ServerSentEvent[] {
    this.buffer += chunk;
    const events: ServerSentEvent[] = [];

    for (;;) {
      const terminator = /\r\n|\r|\n/.exec(this.buffer);
      if (terminator === null) break;
      /*
       * A lone trailing `\r` is held back.
       *
       * It may be the first half of a `\r\n` whose second byte is in the next chunk, and treating it
       * as a terminator would then dispatch on the `\n` that follows — turning one blank line into
       * two and one event into two.
       */
      if (terminator[0] === "\r" && terminator.index === this.buffer.length - 1) break;

      const line = this.buffer.slice(0, terminator.index);
      this.buffer = this.buffer.slice(terminator.index + terminator[0].length);

      if (line.length === 0) {
        const dispatched = this.dispatch();
        if (dispatched !== undefined) events.push(dispatched);
        continue;
      }
      // A comment line. This is what a heartbeat is, and it must complete nothing.
      if (line.startsWith(":")) continue;

      const colon = line.indexOf(":");
      const field = colon === -1 ? line : line.slice(0, colon);
      // One optional space after the colon is part of the format rather than part of the value.
      const rawValue = colon === -1 ? "" : line.slice(colon + 1);
      const value = rawValue.startsWith(" ") ? rawValue.slice(1) : rawValue;

      switch (field) {
        case "event":
          this.name = value;
          break;
        case "data":
          this.data.push(value);
          break;
        case "id":
          // The standard says a NUL in an id is ignored. Nothing here sends one; the rule is cheap.
          if (!value.includes("\u0000")) this.id = value;
          break;
        case "retry": {
          const parsed = Number(value);
          if (Number.isInteger(parsed) && parsed >= 0) this.retryMs = parsed;
          break;
        }
        default:
          // An unknown field is ignored, which is how the format stays extensible.
          break;
      }
    }

    return events;
  }

  /**
   * Turn the accumulated fields into an event, or into nothing.
   *
   * A blank line with no fields behind it dispatches nothing — otherwise the blank line that follows
   * the `retry:` preamble would arrive as an empty `message`, and a client counting events would
   * count one before the stream had sent any.
   */
  private dispatch(): ServerSentEvent | undefined {
    if (this.name === undefined && this.data.length === 0) {
      this.id = undefined;
      return undefined;
    }
    const event: ServerSentEvent = {
      name: this.name ?? "message",
      data: this.data.join("\n"),
      ...(this.id === undefined ? {} : { id: this.id }),
    };
    this.name = undefined;
    this.data = [];
    this.id = undefined;
    return event;
  }
}

/* ------------------------------------------------------------------------- the reconnect policy */

/** The closing event EXP-003 always sends last, as the contract publishes it. */
export interface StreamClosure {
  readonly reason:
    | "max_lifetime"
    | "token_expired"
    | "authorization_revoked"
    | "nothing_left_to_watch"
    | "server_closing";
  readonly reconnectable: boolean;
  readonly resume: string | null;
  readonly retryAfterMs: number;
}

/** What a client does next, which is one of exactly three things. */
export type NextAction =
  /** Wait `afterMs`, then open again with `lastEventId` if there is one. */
  | {
      readonly kind: "reconnect";
      readonly afterMs: number;
      readonly lastEventId?: string;
    }
  /**
   * Get a new access token first, then open again. Never on the retry timer.
   *
   * The one case `reconnectable: false` covers that is recoverable: the token aged out mid-stream
   * and the identity provider will hand over another. `attemptsOnThisToken` is what stops it
   * becoming a loop — a provider that keeps returning the same expired token would otherwise be
   * retried for ever, which is the exact failure `reconnectable` exists to prevent.
   */
  | { readonly kind: "renew_then_reconnect"; readonly lastEventId?: string }
  /** Stop. Either the authorization was withdrawn, or there is nothing left to watch. */
  | { readonly kind: "stop"; readonly because: StreamClosure["reason"] | "token_not_renewable" };

export interface PolicyInput {
  readonly closure: StreamClosure;
  /**
   * The `retry:` the stream itself asked for, if it sent one.
   *
   * Preferred over the closure's `retryAfterMs` only when the two disagree and the stream's is the
   * larger, because the field was written first and a stream that raised it meant it.
   */
  readonly retryMs?: number | undefined;
  /** How many times this client has already tried to open with the token it currently holds. */
  readonly attemptsOnThisToken: number;
}

/**
 * How many times a client may reopen on one token before concluding the token is the problem.
 *
 * Two, not one: the first reopen after `token_expired` is the whole point of renewing, and a second
 * covers the ordinary race where the renewal landed a moment after the stream had already decided.
 * A third would be a loop.
 */
export const MAX_ATTEMPTS_PER_TOKEN = 2;

/**
 * What to do when a stream closes, decided from the closure alone.
 *
 * Written as a total function over the five reasons rather than as a default case, so a reason added
 * to the contract fails the build here instead of being silently retried.
 */
export function nextAfterClosure(input: PolicyInput): NextAction {
  const { closure } = input;
  const afterMs = Math.max(closure.retryAfterMs, input.retryMs ?? 0);
  const resume = closure.resume === null ? {} : { lastEventId: closure.resume };

  switch (closure.reason) {
    case "max_lifetime":
    case "server_closing":
      /*
       * The normal end of a connection, and the normal case.
       *
       * ADR 0024 bounds the lifetime on purpose so that a reconnect is ordinary rather than
       * exceptional, which is also what makes a rolling deploy survivable. `server_closing` is the
       * same shape: another instance will answer.
       */
      return { kind: "reconnect", afterMs, ...resume };

    case "nothing_left_to_watch":
      /*
       * Every offer being watched reached a terminal state.
       *
       * Reconnecting would be refused 400 — EXP-003 refuses a connection that names nothing and can
       * discover nothing — so this stops, and the application opens a new stream when it is told
       * about a new offer. That is the discovery gap, not a retry problem. R-22.
       */
      return { kind: "stop", because: "nothing_left_to_watch" };

    case "token_expired":
      /*
       * `reconnectable: false`, and the flag is load-bearing.
       *
       * Reopening on the retry timer with the same token would be refused every time, for as long
       * as the browser allowed — which is the loop ADR 0024 published the flag to prevent. So this
       * does not reconnect: it asks for a new token first, and gives up if asking did not help.
       */
      return input.attemptsOnThisToken >= MAX_ATTEMPTS_PER_TOKEN
        ? { kind: "stop", because: "token_not_renewable" }
        : { kind: "renew_then_reconnect", ...resume };

    case "authorization_revoked":
      /*
       * The token still verifies and no longer carries what the stream needs.
       *
       * A realm change rather than an expiry, so a new token would carry the same claims and a
       * retry — timed or renewed — would be refused identically. ADR 0024: "a client must not simply
       * retry it."
       */
      return { kind: "stop", because: "authorization_revoked" };
  }
}

/**
 * Read a `stream-closed` payload, refusing anything that is not one.
 *
 * `unknown` in and a narrowed type out, because the generated client types this operation's response
 * as `unknown` — the contract declares `text/event-stream` and no JSON schema, which is correct and
 * is why `getDriverAppOfferStream` must never be called. A malformed payload returns `undefined` and
 * the connection is then treated as having dropped rather than as having closed, which is the safer
 * of the two: a dropped connection reconnects on the timer, where a closure invented from nothing
 * could claim `reconnectable: false` and stop the application for good.
 */
export function readClosure(value: unknown): StreamClosure | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const candidate = value as Record<string, unknown>;
  const reasons = [
    "max_lifetime",
    "token_expired",
    "authorization_revoked",
    "nothing_left_to_watch",
    "server_closing",
  ] as const;
  const reason = reasons.find((known) => known === candidate["reason"]);
  if (reason === undefined) return undefined;
  if (typeof candidate["reconnectable"] !== "boolean") return undefined;
  const resume = candidate["resume"];
  if (resume !== null && typeof resume !== "string") return undefined;
  const retryAfterMs = candidate["retryAfterMs"];
  if (typeof retryAfterMs !== "number" || !Number.isFinite(retryAfterMs)) return undefined;
  return {
    reason,
    reconnectable: candidate["reconnectable"],
    resume,
    retryAfterMs: Math.max(0, retryAfterMs),
  };
}
